import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import {
  Calendar,
  Loader2,
  RefreshCw,
  Lock,
  Unlock,
  Download,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import LoonrunCockpitSection from '@/components/LoonrunCockpitSection';

const LOON_YEARS = [2026, 2027];

const fmtEur = (v) => {
  if (v === null || v === undefined) return '—';
  return `€ ${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Hours/numbers nl-NL (comma decimal). Zero rendered as a muted dash in cells.
const fmtH = (v) => {
  const n = Number(v || 0);
  return n === 0 ? '—' : n.toLocaleString('nl-NL', { maximumFractionDigits: 2 });
};
const fmtHt = (v) => Number(v || 0).toLocaleString('nl-NL', { maximumFractionDigits: 2 });

// flag -> {label, tone}
const FLAG_LABELS = {
  no_uurloon: { label: 'Geen uurloon', tone: 'amber' },
  vacation_balance_negative: { label: 'Negatief verlofsaldo', tone: 'red' },
  vacation_balance_missing: { label: 'Verlofsaldo onbekend', tone: 'amber' },
  salarisperiode_per_maand_mismatch: { label: 'Maandsalaris-mismatch', tone: 'amber' },
  vast_zero_contract_hours: { label: 'Vast zonder contracturen', tone: 'amber' },
  negative_total_clamped: { label: 'Totaal naar 0 begrensd', tone: 'red' },
};

const toneCls = (tone) =>
  tone === 'red'
    ? 'border-rose-300 bg-rose-50 text-rose-700'
    : 'border-amber-300 bg-amber-50 text-amber-700';

const vakHours = (row) =>
  Number(row?.period_totals?.leave_by_type?.vakantie || 0) + Number(row?.vacation_hours_used || 0);
const ziekteHours = (row) => Number(row?.period_totals?.leave_by_type?.ziekte || 0);

// reconcile component -> Dutch label
const RECONCILE_LABELS = {
  clocked: 'Gewerkte uren',
  t100: 'Overwerk 100%',
  t125: 'Overwerk 125%',
  t150: 'Overwerk 150%',
  vacation_hours_used: 'VAK (tekort geboekt)',
  vakantie: 'VAK (vakantie)',
  ziekte: 'ZK (ziekte)',
  bijzonder_verlof: 'Bijzonder verlof',
  onbetaald: 'OBV (onbetaald)',
  km: 'Reiskosten km',
};

export default function Loonperiode() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const isOwnerAdmin = user && (user.role === 'owner' || user.role === 'admin');

  const [locations, setLocations] = useState([]);
  const [loonYear, setLoonYear] = useState(2026);
  const [periodsList, setPeriodsList] = useState([]);
  const [period, setPeriod] = useState(null);

  const [data, setData] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [gaps, setGaps] = useState(null); // {kind: 'lock'|'export', payload}
  const [reconcile, setReconcile] = useState(null); // {locked, in_sync, drift[]}
  const [reconcileOpen, setReconcileOpen] = useState(false);

  // Locations bootstrap
  useEffect(() => {
    (async () => {
      try {
        const { data: locs } = await api.get('/locations');
        const eligible = (locs || []).slice().sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setLocations(eligible);
      } catch {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
  }, [toast]);

  useEffect(() => {
    if (locations.length === 0) return;
    const slugs = locations.map((l) => l.slug);
    // Fall back to the first allowed location when nothing is selected OR the
    // current selection (e.g. a stale ?location= deep-link) isn't permitted for
    // this user — avoids confusing 403s on the period/lock calls.
    if (!selectedSlug || !slugs.includes(selectedSlug)) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  // Load the period list for the chosen loonjaar; pick the period containing
  // today (else the first) when nothing is selected for this year.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPeriodsLoading(true);
      try {
        const { data: resp } = await api.get('/payroll/loonperiodes', { params: { loon_year: loonYear } });
        if (cancelled) return;
        const list = resp?.periods || [];
        setPeriodsList(list);
        const today = new Date();
        const inToday = list.find((p) => {
          const [sd, sm, sy] = String(p.start_date).split('-').map(Number);
          const [ed, em, ey] = String(p.end_date).split('-').map(Number);
          const start = new Date(sy, sm - 1, sd);
          const end = new Date(ey, em - 1, ed, 23, 59, 59);
          return today >= start && today <= end;
        });
        setPeriod(inToday ? inToday.period : (list[0]?.period ?? null));
      } catch {
        if (!cancelled) {
          setPeriodsList([]);
          setPeriod(null);
          toast({ title: 'Kon loonperiodes niet laden', variant: 'destructive' });
        }
      } finally {
        if (!cancelled) setPeriodsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loonYear, toast]);

  const loadRun = useCallback(async () => {
    if (!selectedSlug || !period) return;
    try {
      const { data: resp } = await api.get('/payroll/period/run', {
        params: { location_id: selectedSlug, loon_year: loonYear, period },
      });
      setRun(resp?.run || null);
    } catch {
      setRun(null);
    }
  }, [selectedSlug, loonYear, period]);

  const loadData = useCallback(async () => {
    if (!selectedSlug || !period) return;
    setLoading(true);
    try {
      const { data: resp } = await api.get('/payroll/period', {
        params: { location_id: selectedSlug, loon_year: loonYear, period },
      });
      setData(resp);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kon loonperiode niet laden';
      toast({ title: typeof msg === 'string' ? msg : 'Kon loonperiode niet laden', variant: 'destructive' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSlug, loonYear, period, toast]);

  // Stale-lock / live↔export reconcile (only meaningful when LOCKED).
  const loadReconcile = useCallback(async () => {
    if (!selectedSlug || !period) { setReconcile(null); return; }
    try {
      const { data: resp } = await api.get('/payroll/period/reconcile', {
        params: { location_id: selectedSlug, loon_year: loonYear, period },
      });
      setReconcile(resp?.locked ? resp : null);
    } catch {
      setReconcile(null);
    }
    setReconcileOpen(false);
  }, [selectedSlug, loonYear, period]);

  useEffect(() => { loadData(); loadRun(); loadReconcile(); }, [loadData, loadRun, loadReconcile]);

  const isLocked = run?.status === 'locked';
  const staleExport = isLocked && !!reconcile && reconcile.in_sync === false;
  const rows = useMemo(() => data?.per_employee || [], [data]);

  const selectedPeriodMeta = useMemo(
    () => periodsList.find((p) => p.period === period) || null,
    [periodsList, period],
  );

  // OBV header summary
  const obvSummary = useMemo(() => {
    let medewerkers = 0;
    let dagen = 0;
    rows.forEach((r) => {
      if (Number(r.onbetaald_hours || 0) > 0 || Number(r.onbetaald_days || 0) > 0) {
        medewerkers += 1;
        dagen += Number(r.onbetaald_days || 0);
      }
    });
    return { medewerkers, dagen };
  }, [rows]);

  // Footer sums (computed from rows so VAK matches the export logic)
  const footer = useMemo(() => {
    const f = { clocked: 0, contract: 0, t100: 0, t125: 0, t150: 0, vak: 0, zk: 0, obv: 0, km: 0, total: 0 };
    rows.forEach((r) => {
      f.clocked += Number(r.clocked_hours || 0);
      f.contract += Number(r.period_totals?.contract || 0);
      f.t100 += Number(r.overtime_hours_t100 || 0);
      f.t125 += Number(r.overtime_hours_t125 || 0);
      f.t150 += Number(r.overtime_hours_t150 || 0);
      f.vak += vakHours(r);
      f.zk += ziekteHours(r);
      f.obv += Number(r.onbetaald_hours || 0);
      f.km += Number(r.km_total || 0);
      f.total += Number(r.total_eur || 0);
    });
    return f;
  }, [rows]);

  const doLock = useCallback(async ({ thenDownload = false } = {}) => {
    if (!selectedSlug || !period) return;
    setLockBusy(true);
    try {
      await api.post('/payroll/period/lock', { location_id: selectedSlug, loon_year: loonYear, period });
      toast({ title: 'Loonperiode vergrendeld', description: `Periode ${period} · ${loonYear}` });
      await loadRun();
      await loadData();
      await loadReconcile();
      if (thenDownload) await doDownload();
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data;
      if (status === 422) {
        setGaps({ kind: 'lock', payload: payload || {} });
      } else if (status === 409) {
        toast({ title: payload?.detail || 'Al vergrendeld', variant: 'destructive' });
        await loadRun();
      } else {
        toast({ title: payload?.detail || 'Vergrendelen mislukt', variant: 'destructive' });
      }
    } finally {
      setLockBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlug, loonYear, period, loadRun, loadData, toast]);

  const doUnlock = useCallback(async () => {
    setConfirmUnlock(false);
    setLockBusy(true);
    try {
      await api.post('/payroll/period/unlock', { location_id: selectedSlug, loon_year: loonYear, period });
      toast({ title: 'Loonperiode ontgrendeld', description: 'De afboeking is teruggedraaid.' });
      await loadRun();
      await loadData();
      await loadReconcile();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Ontgrendelen mislukt', variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  }, [selectedSlug, loonYear, period, loadRun, loadData, loadReconcile, toast]);

  const doDownload = useCallback(async () => {
    if (!selectedSlug || !period) return;
    setExportBusy(true);
    try {
      const resp = await api.get('/payroll/period/export', {
        params: { location_id: selectedSlug, loon_year: loonYear, period },
        responseType: 'blob',
      });
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `mutaties_TW_${selectedSlug}_${loonYear}_P${period}.xlsx`;
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'mutaties TW gedownload', description: filename });
    } catch (e) {
      const status = e?.response?.status;
      let payload = e?.response?.data;
      if (payload instanceof Blob) {
        try { payload = JSON.parse(await payload.text()); } catch { payload = {}; }
      }
      if (status === 409) {
        toast({ title: 'Export geblokkeerd', description: payload?.detail || 'Eerst vergrendelen.', variant: 'destructive' });
        loadReconcile();
      } else if (status === 422) {
        setGaps({ kind: 'export', payload: payload || {} });
      } else {
        toast({ title: 'Export mislukt', variant: 'destructive' });
      }
    } finally {
      setExportBusy(false);
    }
  }, [selectedSlug, loonYear, period, toast, loadReconcile]);

  return (
    <div className="space-y-5" data-testid="page-loonperiode">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Loonperiode</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Afrekening per loonperiode (4–5 hele weken) op de <strong>thuislocatie</strong> van de medewerker.
            Vergrendel om te boeken en download de <em>mutaties TW</em> voor Exact.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="loonperiode-location-select"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            disabled={loading}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none disabled:opacity-50"
          >
            {locations.map((loc) => (
              <option key={loc.slug} value={loc.slug}>{loc.name}</option>
            ))}
          </select>
          <select
            data-testid="loonperiode-year-select"
            value={loonYear}
            onChange={(e) => setLoonYear(Number(e.target.value))}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none"
          >
            {LOON_YEARS.map((y) => (
              <option key={y} value={y}>Loonjaar {y}</option>
            ))}
          </select>
          <select
            data-testid="loonperiode-period-select"
            value={period ?? ''}
            onChange={(e) => setPeriod(Number(e.target.value))}
            disabled={periodsLoading || !periodsList.length}
            className="h-9 min-w-[260px] rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none disabled:opacity-50"
          >
            {periodsList.map((p) => (
              <option key={p.period} value={p.period}>
                Periode {p.period} · {p.weeks_label} · t/m {p.end_date}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="loonperiode-refresh"
            onClick={() => { loadData(); loadRun(); loadReconcile(); }}
            disabled={loading}
            className="h-9"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="ml-1.5">Verversen</span>
          </Button>
        </div>
      </div>

      {/* Loonrun-cockpit: status van alle locaties voor deze periode */}
      {loonYear && period && (
        <LoonrunCockpitSection
          loonYear={loonYear}
          period={period}
          onLockedOrExported={() => { loadRun(); loadData(); loadReconcile(); }}
        />
      )}

      {/* Status + actions bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {isLocked ? (
            <Badge data-testid="loonperiode-status-badge" className="h-7 gap-1 bg-emerald-100 px-3 text-emerald-700 hover:bg-emerald-100">
              <Lock size={12} /> Vergrendeld
            </Badge>
          ) : (
            <Badge data-testid="loonperiode-status-badge" variant="outline" className="h-7 gap-1 px-3 text-slate-600">
              <Unlock size={12} /> Open
            </Badge>
          )}
          {isLocked && reconcile && (
            reconcile.in_sync ? (
              <Badge
                data-testid="loonperiode-reconcile-badge"
                className="h-7 gap-1 bg-emerald-50 px-3 text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircle2 size={12} /> Scherm komt overeen met de export
              </Badge>
            ) : (
              <Badge
                data-testid="loonperiode-reconcile-badge"
                className="h-7 gap-1 bg-amber-100 px-3 text-amber-800 hover:bg-amber-100"
              >
                <AlertTriangle size={12} /> Data gewijzigd sinds vergrendeling
              </Badge>
            )
          )}
          {isLocked && run?.exported_at && (
            <Badge data-testid="loonperiode-exported-badge" variant="outline" className="h-7 gap-1 border-sky-300 bg-sky-50 px-3 text-sky-700">
              <Download size={12} /> Aangeleverd op {new Date(run.exported_at).toLocaleDateString('nl-NL')}
            </Badge>
          )}
          {selectedPeriodMeta && (
            <span className="text-sm text-slate-500" data-testid="loonperiode-range-label">
              {selectedPeriodMeta.start_date} – {selectedPeriodMeta.end_date}
            </span>
          )}
          {obvSummary.medewerkers > 0 && (
            <Badge variant="outline" data-testid="loonperiode-obv-summary" className="h-7 gap-1 border-sky-300 bg-sky-50 px-3 text-sky-700">
              OBV: {obvSummary.medewerkers} medewerker(s) · {fmtHt(obvSummary.dagen)} dagen
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isLocked ? (
            <Button
              type="button"
              size="sm"
              data-testid="loonperiode-lock-btn"
              onClick={() => doLock()}
              disabled={lockBusy || loading || !period}
              className="h-9 bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              <span className="ml-1.5">Vergrendelen</span>
            </Button>
          ) : (
            isOwnerAdmin && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="loonperiode-unlock-btn"
                onClick={() => setConfirmUnlock(true)}
                disabled={lockBusy || loading}
                className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
                <span className="ml-1.5">Ontgrendelen</span>
              </Button>
            )
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="loonperiode-download-btn"
            onClick={doDownload}
            disabled={exportBusy || loading || !period || staleExport}
            title={staleExport ? 'Export verouderd — ontgrendel en vergrendel opnieuw.' : undefined}
            className="h-9"
          >
            {exportBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span className="ml-1.5">Download mutaties TW</span>
          </Button>
          {staleExport && (
            <span className="text-xs text-amber-700" data-testid="loonperiode-download-stale-reason">
              Export verouderd — ontgrendel en vergrendel opnieuw.
            </span>
          )}
          {!isLocked && (
            <Button
              type="button"
              size="sm"
              data-testid="loonperiode-lock-download-btn"
              onClick={() => doLock({ thenDownload: true })}
              disabled={lockBusy || exportBusy || loading || !period}
              className="h-9 bg-brand-amber text-brand-navy hover:bg-brand-amber/90"
            >
              <Zap size={14} />
              <span className="ml-1.5">Vergrendelen → download</span>
            </Button>
          )}
        </div>
      </div>

      {/* Stale-lock / reconcile banner (only when LOCKED and drift detected) */}
      {isLocked && reconcile && !reconcile.in_sync && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="loonperiode-reconcile-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                Data gewijzigd sinds vergrendeling — de download is niet meer actueel.
              </p>
              <p className="mt-0.5 text-amber-700">
                Ontgrendel en vergrendel opnieuw om de <em>mutaties TW</em> bij te werken.
                {' '}
                <button
                  type="button"
                  data-testid="loonperiode-reconcile-toggle"
                  onClick={() => setReconcileOpen((o) => !o)}
                  className="inline-flex items-center gap-1 font-medium underline"
                >
                  {reconcile.drift.length} verschil(len)
                  <ChevronDown size={12} className={reconcileOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                </button>
              </p>
              {reconcileOpen && (
                <div className="mt-2 overflow-hidden rounded-lg border border-amber-200 bg-white">
                  <table className="w-full text-xs" data-testid="loonperiode-reconcile-list">
                    <thead>
                      <tr className="bg-amber-100/60 text-left text-amber-900">
                        <th className="px-3 py-1.5 font-medium">Medewerker</th>
                        <th className="px-3 py-1.5 font-medium">Component</th>
                        <th className="px-3 py-1.5 text-right font-medium">Vergrendeld</th>
                        <th className="px-3 py-1.5 text-right font-medium">Nu (live)</th>
                        <th className="px-3 py-1.5 text-right font-medium">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconcile.drift.map((d, i) => (
                        <tr key={`${d.employee_id}-${d.component}-${i}`} className="border-t border-amber-100">
                          <td className="px-3 py-1.5">{d.name || d.employee_id}</td>
                          <td className="px-3 py-1.5">{RECONCILE_LABELS[d.component] || d.component}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtHt(d.frozen)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{fmtHt(d.live)}</td>
                          <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                            {d.delta > 0 ? '+' : ''}{fmtHt(d.delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="loonperiode-table">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-center text-slate-500">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : !period ? (
          <div className="p-10 text-center">
            <Calendar size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Kies een loonperiode.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="loonperiode-empty">
            <Calendar size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen medewerkers met deze locatie als thuislocatie in deze periode.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[260px]">Naam</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Gewerkt</TableHead>
                <TableHead className="text-right">Contract</TableHead>
                <TableHead className="text-right">100%</TableHead>
                <TableHead className="text-right">125%</TableHead>
                <TableHead className="text-right">150%</TableHead>
                <TableHead className="text-right">VAK</TableHead>
                <TableHead className="text-right">ZK</TableHead>
                <TableHead className="text-right">OBV</TableHead>
                <TableHead className="text-right">Km</TableHead>
                <TableHead className="text-right">Totaal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const flags = row.flags || [];
                return (
                  <TableRow key={row.employee_id} data-testid={`loonperiode-row-${row.employee_id}`}>
                    <TableCell>
                      <div className="leading-tight">
                        <p className="text-sm font-medium text-slate-900">{row.name}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {flags.map((fl) => {
                            const meta = FLAG_LABELS[fl] || { label: fl, tone: 'amber' };
                            const showBal = fl === 'vacation_balance_negative' && row.vacation_balance_after != null;
                            return (
                              <Badge
                                key={fl}
                                variant="outline"
                                data-testid={`flag-${fl}-${row.employee_id}`}
                                className={`text-[10px] ${toneCls(meta.tone)}`}
                              >
                                {meta.tone === 'red'
                                  ? <AlertTriangle size={10} className="mr-0.5" />
                                  : <AlertCircle size={10} className="mr-0.5" />}
                                {meta.label}
                                {showBal ? ` (${fmtHt(row.vacation_balance_after)} u)` : ''}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={row.is_oproep_like ? 'border-slate-300 text-slate-600' : 'border-brand-navy/30 bg-brand-navy/5 text-brand-navy'}>
                        {row.is_oproep_like ? 'Oproep' : 'Vast'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtH(row.clocked_hours)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-slate-500">{fmtH(row.period_totals?.contract)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtH(row.overtime_hours_t100)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-amber-700">{fmtH(row.overtime_hours_t125)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-amber-700">{fmtH(row.overtime_hours_t150)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtH(vakHours(row))}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtH(ziekteHours(row))}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtH(row.onbetaald_hours)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-slate-500">{fmtH(row.km_total)}</TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums text-brand-navy">{fmtEur(row.total_eur)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-slate-50" data-testid="loonperiode-totals-row">
                <TableCell className="font-semibold">Totaal</TableCell>
                <TableCell className="text-xs text-slate-500">{rows.length} mw</TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">{fmtHt(footer.clocked)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-slate-500">{fmtHt(footer.contract)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.t100)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.t125)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.t150)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.vak)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.zk)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{fmtHt(footer.obv)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-slate-500">{fmtHt(footer.km)}</TableCell>
                <TableCell className="text-right text-sm font-bold tabular-nums text-brand-navy">{fmtEur(footer.total)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </div>

      {/* Gaps dialog (lock 422 OR export 422) */}
      <Dialog open={!!gaps} onOpenChange={(open) => { if (!open) setGaps(null); }}>
        <DialogContent data-testid="loonperiode-gaps-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle size={18} /> Eerst oplossen vóór {gaps?.kind === 'export' ? 'exporteren' : 'vergrendelen'}
            </DialogTitle>
            <DialogDescription>
              {gaps?.kind === 'export'
                ? 'De export naar Exact mist gegevens. Vul deze eerst aan:'
                : 'De loonperiode kan nog niet worden vergrendeld. Los deze punten op:'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-4 overflow-y-auto py-1 text-sm">
            {gaps?.payload?.missing_werkgever_code && (
              <div data-testid="gap-werkgever">
                <p className="font-medium text-slate-800">Geen werkgevercode voor deze locatie</p>
                <p className="text-xs text-slate-500">
                  Stel de werkgevercode in voor <strong>{gaps.payload.missing_werkgever_code}</strong> via Instellingen → Salaris-export.
                </p>
              </div>
            )}
            {Array.isArray(gaps?.payload?.missing_uurloon) && gaps.payload.missing_uurloon.length > 0 && (
              <div data-testid="gap-uurloon">
                <p className="font-medium text-slate-800">
                  Ontbrekende loonbasis — uurloon bij oproep / brutosalaris bij vast ({gaps.payload.missing_uurloon.length})
                </p>
                <ul className="mt-1 list-disc pl-5 text-slate-600">
                  {gaps.payload.missing_uurloon.map((m) => (
                    <li key={m.employee_id}>
                      {m.name || m.employee_id}
                      {m.reason ? ` — ${m.reason === 'brutosalaris' ? 'brutosalaris ontbreekt' : 'uurloon ontbreekt'}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(gaps?.payload?.missing_medewerkernummer) && gaps.payload.missing_medewerkernummer.length > 0 && (
              <div data-testid="gap-medewerkernummer">
                <p className="font-medium text-slate-800">Medewerkernummer ontbreekt ({gaps.payload.missing_medewerkernummer.length})</p>
                <ul className="mt-1 list-disc pl-5 text-slate-600">
                  {gaps.payload.missing_medewerkernummer.map((m) => (
                    <li key={m.employee_id}>{m.name || m.employee_id}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(gaps?.payload?.niet_uitgeklokt) && gaps.payload.niet_uitgeklokt.length > 0 && (
              <div data-testid="gap-uitgeklokt">
                <p className="font-medium text-slate-800">Niet uitgeklokt ({gaps.payload.niet_uitgeklokt.length})</p>
                <ul className="mt-1 list-disc pl-5 text-slate-600">
                  {gaps.payload.niet_uitgeklokt.map((m, i) => (
                    <li key={`${m.employee_id}-${m.date}-${i}`}>
                      {m.name || m.employee_id}{m.date ? ` — ${m.date}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" data-testid="loonperiode-gaps-close" onClick={() => setGaps(null)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock confirm */}
      <AlertDialog open={confirmUnlock} onOpenChange={setConfirmUnlock}>
        <AlertDialogContent data-testid="loonperiode-unlock-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Loonperiode ontgrendelen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je het zeker? Dit draait de afboeking terug (systeem-VAK wordt verwijderd en het
              verlofsaldo wordt hersteld). Daarna kan de periode opnieuw worden vergrendeld.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="loonperiode-unlock-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="loonperiode-unlock-confirm-btn"
              onClick={doUnlock}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Ontgrendelen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
